import type BackOfficeConnector from "data/backOfficeConnector";
import type EventBus from "services/eventBus";
import RepositoryBase from "./repositoryBase";
import type { IPagination, IPagedResults } from "data/types";
import { serializeFilters } from "utils/filters";
import type SearchAccountingDocumentsInventoriesRequest from "entities/searchAccountingDocumentsInventoriesRequest";
import AccountingDocumentsInventoriesResponse from "entities/acccountingDocumentsInventoriesResponse";
import AccountingDocumentsInventoriesTotalCountResponse from "entities/AccountingDocumentsInventoriesTotalCountResponse";
import AccountingDocumentsInventoryDetailResponse from "entities/accountingDocumentsInventoryDetailResponse";
import type UpdateAccountingDocumentsInventoryRequest from "entities/updateAccountingDocumentsInventoryRequest";
import type CreateAccountingDocumentsInventoryRequest from "entities/createAccountingDocumentsInventoryRequest";
import type AccountingDocumentsInventoryAccountFilesResponse from "entities/accountingDocumentsInventoryAccountFilesResponse";
import type { AxiosResponse } from "axios";

export default class AccountingDocumentsInventoryRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }
  search(
    filters: SearchAccountingDocumentsInventoriesRequest,
    pagination: IPagination
  ): Promise<AccountingDocumentsInventoriesResponse> {
    return this.callApi(
      api =>
        api
          .all("accounting")
          .all("documents_inventory_search")
          .postEntity({ ...serializeFilters(filters, false), ...pagination }, AccountingDocumentsInventoriesResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  searchTotalCount(
    filters: SearchAccountingDocumentsInventoriesRequest,
    pagination: IPagination,
    abortSignal: AbortSignal
  ): Promise<IPagedResults<AccountingDocumentsInventoriesTotalCountResponse>> {
    return this.callApi(
      api =>
        api
          .all("accounting")
          .all("account_balance_check_request_search")
          .postPagedEntities(
            { ...serializeFilters(filters, false), ...pagination },
            AccountingDocumentsInventoriesTotalCountResponse,
            abortSignal
          ),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  get(id: string) {
    return this.callApi(
      api => api.all("accounting").one("documents_inventories", id).getEntity(AccountingDocumentsInventoryDetailResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  update(id: string, payload: UpdateAccountingDocumentsInventoryRequest) {
    return this.callApi(api => api.all("accounting").one("documents_inventories", id).putOnly(serializeFilters(payload, false)), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  delete(id: string) {
    return this.callApi(api => api.all("accounting").one("documents_inventories", id).delete({}), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  checks(id: string) {
    return this.callApi(api => api.all("accounting").one("documents_inventories", id).all("checks").postOnly({}), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  closures(id: string) {
    return this.callApi(api => api.all("accounting").one("documents_inventories", id).all("closures").postOnly({}), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  checkResultFile(id: string): Promise<AxiosResponse<Blob>> {
    return this.callApi(api => api.all("accounting").one("documents_inventories", id).all("check_result_file").getRaw() as any, {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  create(entity: CreateAccountingDocumentsInventoryRequest) {
    return this.callApi(api => api.all("accounting").all("documents_inventories").post(entity) as any, {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  uploadAccountFiles(documentInventoryId: string, files: File[]): Promise<AccountingDocumentsInventoryAccountFilesResponse> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append("formFiles", file);
    });

    return this.callApi(
      api =>
        api
          .all("accounting")
          .one("documents_inventories", documentInventoryId)
          .all("account_files")
          .postData(formData)
          .then(response => response.json()),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getAccountFile(documentInventoryId: string, inventoryAccountId: string): Promise<AxiosResponse<File>> {
    return this.callApi(
      api =>
        api
          .all("accounting")
          .one("documents_inventories", documentInventoryId)
          .one("account_files", inventoryAccountId)
          .getRaw() as any,
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  deleteAccountFile(documentInventoryId: string, inventoryAccountId: string) {
    return this.callApi(
      api =>
        api
          .all("accounting")
          .one("documents_inventories", documentInventoryId)
          .one("account_files", inventoryAccountId)
          .delete({}),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }
}
